import React, { useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import CarouselLeftSmall from '@rover/icons/svg/carousel-left-small.svg';
import { Box, Button, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import DialogModal from '@rover/react-lib/src/components/modals/DialogModal';
import { useConfirmWebViewClose } from '@rover/react-lib/src/pages/provider-profile/hooks/useConfirmWebViewClose';

type Props = {
  href: string;
  headerText?: string;
  modalText?: string;
  cancelButtonText?: string;
  submitButtonText?: string;
  skipModal?: boolean;
  onToggleOpen?: (isOpen: boolean) => void;
};

/**
 * Back button with unsaved changes warning modal.
 * Non-Redux version that uses skipModal to control modal behavior.
 * Use this for modern forms that don't rely on Redux for state management.
 * Set skipModal={false} to show the modal, skipModal={true} to skip it.
 */
export const BackButtonWithFormState = ({
  href,
  headerText: headerTextProp,
  modalText: modalTextProp,
  cancelButtonText: cancelButtonTextProp,
  submitButtonText: submitButtonTextProp,
  skipModal = true,
  onToggleOpen,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const headerText = headerTextProp === undefined ? t`Unsaved changes` : headerTextProp;
  const modalText =
    modalTextProp === undefined
      ? t`Any unsaved changes will be lost. Are you sure you want to leave this page?`
      : modalTextProp;
  const cancelButtonText = cancelButtonTextProp === undefined ? t`Cancel` : cancelButtonTextProp;
  const submitButtonText =
    submitButtonTextProp === undefined ? t`Leave page` : submitButtonTextProp;

  // `onToggleOpen` fires only on a real change: the fixed footers wire it to a
  // Redux action that flips its own flag rather than reading the argument, so
  // reporting an open the modal already had would leave the footer inverted --
  // its Back and Save controls hidden once the dialog is gone.
  const setModalOpen = (nextIsOpen: boolean): void => {
    if (nextIsOpen === isOpen) return;

    setIsOpen(nextIsOpen);

    if (onToggleOpen) {
      onToggleOpen(nextIsOpen);
    }
  };

  const toggleIsOpen = (): void => setModalOpen(!isOpen);

  const navigateAway = (): void => window.location.assign(href);

  const handleBackClick = (): void => {
    if (!skipModal) {
      toggleIsOpen();
    } else {
      navigateAway();
    }
  };

  // The apps' close control leaves this page without going through the button
  // above, so it asks here instead. Declining is the same answer the button
  // gives itself: nothing to lose, so let the web view close.
  useConfirmWebViewClose((): boolean => {
    if (skipModal) return false;

    setModalOpen(true);
    return true;
  });

  return (
    <Box>
      <Box textAlign="left">
        <Button
          icon={CarouselLeftSmall}
          variant="flat"
          onPress={handleBackClick}
          size="small"
          pl="0x"
          sx={{
            '&:hover,&:focus': {
              color: theme.colors.interactive_link.primary_pressed,
              backgroundColor: theme.background.interactive.primary_base,
            },
            svg: {
              width: '24px',
              height: '24px',
            },
          }}
        >
          <Text size="100" fontWeight="regular">
            {t`Back`}
          </Text>
        </Button>
      </Box>
      <DialogModal
        headerText={headerText}
        isOpen={isOpen}
        onRequestClose={toggleIsOpen}
        cancelButtonText={cancelButtonText}
        onClickCancel={toggleIsOpen}
        submitButtonText={submitButtonText}
        onClickSubmit={navigateAway}
      >
        {modalText}
      </DialogModal>
    </Box>
  );
};

export default BackButtonWithFormState;
